import React, { useState, useEffect } from 'react';
import { 
  Calendar, AlertCircle, CheckCircle, Clock, Plus, Edit, 
  Trash2, Archive, RotateCcw, X, Save, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import api from '../../utils/api';
import { toast } from 'sonner';

const TaxDeadlines = () => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline_date: '',
    category: 'individual',
    deadline_type: '',
    is_recurring: false,
    notes: ''
  });

  const categories = [
    { value: 'individual', label: 'Individual', color: 'bg-blue-100 text-blue-700' },
    { value: 'business', label: 'Business', color: 'bg-green-100 text-green-700' },
    { value: 'quarterly', label: 'Quarterly', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'annual', label: 'Annual', color: 'bg-red-100 text-red-700' }
  ];

  useEffect(() => {
    fetchDeadlines();
  }, []);

  const fetchDeadlines = async () => {
    try {
      const response = await api.get('/tax-deadlines');
      setDeadlines(response.data);
    } catch (error) {
      console.error('Failed to fetch deadlines:', error);
      toast.error('Failed to load tax deadlines');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      deadline_date: '',
      category: 'individual',
      deadline_type: '',
      is_recurring: false,
      notes: ''
    });
  };

  const handleAddNew = () => {
    resetForm();
    setEditingDeadline(null);
    setShowAddModal(true);
  };

  const handleEdit = (deadline) => {
    setFormData({
      title: deadline.title,
      description: deadline.description,
      deadline_date: deadline.deadline_date.split('T')[0],
      category: deadline.category,
      deadline_type: deadline.deadline_type || '',
      is_recurring: deadline.is_recurring || false,
      notes: deadline.notes || ''
    });
    setEditingDeadline(deadline);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.deadline_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        deadline_date: new Date(formData.deadline_date).toISOString()
      };

      if (editingDeadline) {
        await api.put(`/tax-deadlines/${editingDeadline.id}`, payload);
        toast.success('Tax deadline updated');
      } else {
        await api.post('/tax-deadlines', payload);
        toast.success('Tax deadline created');
      }
      
      setShowAddModal(false);
      resetForm();
      setEditingDeadline(null);
      fetchDeadlines();
    } catch (error) {
      console.error('Failed to save deadline:', error);
      toast.error(error.response?.data?.detail || 'Failed to save deadline');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/tax-deadlines/${deleteConfirm.id}`);
      toast.success('Tax deadline deleted');
      setDeleteConfirm(null);
      fetchDeadlines();
    } catch (error) {
      console.error('Failed to delete deadline:', error);
      toast.error('Failed to delete deadline');
    }
  };

  const handleArchive = async (deadline) => {
    try {
      if (deadline.archived) {
        await api.put(`/tax-deadlines/${deadline.id}/unarchive`);
        toast.success('Tax deadline restored');
      } else {
        await api.put(`/tax-deadlines/${deadline.id}/archive`);
        toast.success('Tax deadline archived');
      }
      fetchDeadlines();
    } catch (error) {
      console.error('Failed to archive/unarchive deadline:', error);
      toast.error('Failed to update deadline');
    }
  };

  const getDaysUntil = (deadlineDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineDate);
    deadline.setHours(0, 0, 0, 0);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineStatus = (deadlineDate) => {
    const days = getDaysUntil(deadlineDate);
    if (days < 0) return { status: 'overdue', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' };
    if (days <= 7) return { status: 'urgent', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' };
    if (days <= 30) return { status: 'upcoming', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' };
    return { status: 'future', color: 'text-gray-600', bgColor: 'bg-white', borderColor: 'border-gray-200' };
  };

  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'bg-gray-100 text-gray-700';
  };

  const filteredDeadlines = deadlines.filter(d => showArchived ? d.archived : !d.archived);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="deadlines-title">Tax Deadlines</h2>
          <p className="text-gray-600">Manage important tax filing and payment deadlines</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? 'bg-gray-100' : ''}
            data-testid="toggle-archived-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showArchived ? 'Show Active' : 'Show Archived'}
          </Button>
          
          <Button 
            onClick={handleAddNew}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
            data-testid="add-deadline-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Deadline
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {deadlines.filter(d => !d.archived).length}
          </div>
          <div className="text-sm text-gray-600">Total Active</div>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-4 border-red-500">
          <div className="text-2xl font-bold text-red-600">
            {deadlines.filter(d => !d.archived && getDaysUntil(d.deadline_date) < 0).length}
          </div>
          <div className="text-sm text-gray-600">Overdue</div>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-4 border-orange-500">
          <div className="text-2xl font-bold text-orange-600">
            {deadlines.filter(d => !d.archived && getDaysUntil(d.deadline_date) >= 0 && getDaysUntil(d.deadline_date) <= 7).length}
          </div>
          <div className="text-sm text-gray-600">This Week</div>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-4 border-yellow-500">
          <div className="text-2xl font-bold text-yellow-600">
            {deadlines.filter(d => !d.archived && getDaysUntil(d.deadline_date) > 7 && getDaysUntil(d.deadline_date) <= 30).length}
          </div>
          <div className="text-sm text-gray-600">This Month</div>
        </div>
      </div>

      {/* Deadlines List */}
      <div className="space-y-4">
        {filteredDeadlines.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {showArchived ? 'No archived deadlines' : 'No active tax deadlines'}
            </p>
            {!showArchived && (
              <Button onClick={handleAddNew} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Deadline
              </Button>
            )}
          </div>
        ) : (
          filteredDeadlines.map((deadline, index) => {
            const daysUntil = getDaysUntil(deadline.deadline_date);
            const status = getDeadlineStatus(deadline.deadline_date);

            return (
              <div
                key={deadline.id}
                className={`glass rounded-xl p-6 transition-all border-l-4 ${status.borderColor} ${
                  deadline.archived ? 'opacity-60' : 'hover-lift'
                } ${status.bgColor}`}
                data-testid={`deadline-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Calendar className={`w-5 h-5 ${status.color}`} />
                      <h3 className="text-lg font-bold text-gray-900">{deadline.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryStyle(deadline.category)}`}>
                        {deadline.category}
                      </span>
                      {deadline.is_recurring && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          Recurring
                        </span>
                      )}
                      {deadline.archived && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                          Archived
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">{deadline.description}</p>
                    
                    {deadline.deadline_type && (
                      <p className="text-sm text-gray-500 mb-2">Type: {deadline.deadline_type}</p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center">
                        <Clock className={`w-4 h-4 mr-1 ${status.color}`} />
                        <span className={`font-medium ${status.color}`}>
                          {new Date(deadline.deadline_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      
                      {!deadline.archived && (
                        <div className={`flex items-center ${status.color}`}>
                          {daysUntil < 0 ? (
                            <>
                              <AlertCircle className="w-4 h-4 mr-1" />
                              <span className="font-medium">Overdue by {Math.abs(daysUntil)} days</span>
                            </>
                          ) : daysUntil === 0 ? (
                            <>
                              <AlertCircle className="w-4 h-4 mr-1" />
                              <span className="font-medium">Due Today!</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              <span className="font-medium">{daysUntil} days remaining</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(deadline)}
                      className="text-gray-600 hover:text-teal-600"
                      data-testid={`edit-deadline-${index}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleArchive(deadline)}
                      className="text-gray-600 hover:text-yellow-600"
                      data-testid={`archive-deadline-${index}`}
                      title={deadline.archived ? 'Restore' : 'Archive'}
                    >
                      {deadline.archived ? (
                        <RotateCcw className="w-4 h-4" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(deadline)}
                      className="text-gray-600 hover:text-red-600"
                      data-testid={`delete-deadline-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingDeadline ? 'Edit Tax Deadline' : 'Add New Tax Deadline'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                data-testid="deadline-title-input"
                placeholder="e.g., Q1 Estimated Tax Payment"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                data-testid="deadline-description-input"
                placeholder="Describe what needs to be done..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="deadline_date">Deadline Date *</Label>
                <Input
                  id="deadline_date"
                  data-testid="deadline-date-input"
                  type="date"
                  value={formData.deadline_date}
                  onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  data-testid="deadline-category-select"
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="deadline_type">Deadline Type (Optional)</Label>
              <Input
                id="deadline_type"
                data-testid="deadline-type-input"
                placeholder="e.g., Tax Filing, Payment, Extension"
                value={formData.deadline_type}
                onChange={(e) => setFormData({ ...formData, deadline_type: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                data-testid="deadline-notes-input"
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_recurring"
                data-testid="deadline-recurring-checkbox"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_recurring" className="text-sm cursor-pointer">
                This is a recurring deadline
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              data-testid="save-deadline-btn"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingDeadline ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Tax Deadline?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>"{deleteConfirm?.title}"</strong>? 
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxDeadlines;
